import React, {
    createContext,
    memo,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from 'react'
import { Box, Chip } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import HorizontalScrollButtons from '../HorizontalScrollWrapper/horizontalScrollWrapper'

export const OVERVIEW_CONTENT_SCROLL_ID = 'overview-content-scroll'

export const OVERVIEW_SECTION_IDS = {
    components: 'components',
    community: 'community-screens',
    seasonals: 'seasonals',
    technicals: 'technicals',
    oiAnalysis: 'oi-analysis'
} as const

export const OVERVIEW_SECTION_ORDER = {
    components: 10,
    fieldGroupStart: 20,
    community: 100,
    seasonals: 110,
    technicals: 120,
    oiAnalysis: 130
} as const

export type OverviewNavSection = {
    id: string
    label: string
    order: number
}

type OverviewNavActions = {
    register: (section: OverviewNavSection) => void
    unregister: (id: string) => void
}

const OverviewNavActionsContext = createContext<OverviewNavActions | null>(null)
const OverviewNavSectionsContext = createContext<OverviewNavSection[]>([])

export function overviewSectionDomId(id: string) {
    return `overview-section-${id}`
}

export function slugifyOverviewSection(name: string) {
    return name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

export function OverviewNavProvider({ children }: { children: ReactNode }) {
    const [sectionsById, setSectionsById] = useState<Record<string, OverviewNavSection>>({})

    const register = useCallback((section: OverviewNavSection) => {
        setSectionsById((prev) => {
            const existing = prev[section.id]
            if (existing && existing.label === section.label && existing.order === section.order) {
                return prev
            }
            return { ...prev, [section.id]: section }
        })
    }, [])

    const unregister = useCallback((id: string) => {
        setSectionsById((prev) => {
            if (!(id in prev)) return prev
            const next = { ...prev }
            delete next[id]
            return next
        })
    }, [])

    const actions = useMemo(() => ({ register, unregister }), [register, unregister])
    const sections = useMemo(
        () => Object.values(sectionsById).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)),
        [sectionsById]
    )

    return (
        <OverviewNavActionsContext.Provider value={actions}>
            <OverviewNavSectionsContext.Provider value={sections}>{children}</OverviewNavSectionsContext.Provider>
        </OverviewNavActionsContext.Provider>
    )
}

export type OverviewSectionProps = {
    id: string
    label: string
    order: number
    children: ReactNode
}

export const OverviewSection = memo(({ id, label, order, children }: OverviewSectionProps) => {
    const actions = useContext(OverviewNavActionsContext)

    useLayoutEffect(() => {
        actions?.register({ id, label, order })
        return () => actions?.unregister(id)
    }, [actions, id, label, order])

    return (
        <Box
            id={overviewSectionDomId(id)}
            data-overview-section={id}
            data-overview-section-label={label}
            sx={{ scrollMarginTop: 40 }}
        >
            {children}
        </Box>
    )
})

const useStyles = makeStyles()((theme) => ({
    wrap: {
        position: 'sticky',
        top: 0,
        zIndex: 120,
        flexShrink: 0,
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    bar: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: theme.spacing(0.75),
        padding: theme.spacing(1, 2),
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none'
        }
    },
    chip: {
        height: 24,
        fontSize: 11,
        flexShrink: 0,
        borderRadius: theme.shape.borderRadius,
        '& .MuiChip-label': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1)
        },
        '&.MuiChip-outlined:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover
        }
    },
    chipActive: {
        fontWeight: 500
    }
}))

const PROGRAMMATIC_SCROLL_MS = 500

export const OverviewSectionTabs = memo(() => {
    const sections = useContext(OverviewNavSectionsContext)
    const { classes, cx } = useStyles()
    const wrapRef = useRef<HTMLDivElement>(null)
    const barRef = useRef<HTMLDivElement>(undefined)
    const chipRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const [activeId, setActiveId] = useState<string | null>(null)
    const programmatic = useRef(false)
    const programmaticTimer = useRef<number | undefined>(undefined)
    const pinnedId = useRef<string | null>(null)

    const getScrollRoot = useCallback(() => {
        return (
            (wrapRef.current?.closest(`#${OVERVIEW_CONTENT_SCROLL_ID}`) as HTMLElement | null) ??
            (barRef.current?.closest(`#${OVERVIEW_CONTENT_SCROLL_ID}`) as HTMLElement | null) ??
            document.getElementById(OVERVIEW_CONTENT_SCROLL_ID)
        )
    }, [])

    const syncActive = useCallback(() => {
        const root = getScrollRoot()
        if (!root || sections.length === 0) return
        if (pinnedId.current) return

        const tabsHeight = wrapRef.current?.offsetHeight ?? barRef.current?.offsetHeight ?? 0
        const rootTop = root.getBoundingClientRect().top
        const offset = tabsHeight + 4

        if (root.scrollTop + root.clientHeight >= root.scrollHeight - 2) {
            setActiveId(sections[sections.length - 1].id)
            return
        }

        let next = sections[0].id
        for (const section of sections) {
            const el = document.getElementById(overviewSectionDomId(section.id))
            if (!el) continue
            if (el.getBoundingClientRect().top - rootTop <= offset) {
                next = section.id
            }
        }
        setActiveId(next)
    }, [getScrollRoot, sections])

    useEffect(() => {
        if (sections.length === 0) {
            setActiveId(null)
            return
        }
        setActiveId((current) => {
            if (current && sections.some((section) => section.id === current)) return current
            return sections[0].id
        })
    }, [sections])

    useEffect(() => {
        const root = getScrollRoot()
        if (!root) return

        const onScroll = () => {
            if (programmatic.current) {
                window.clearTimeout(programmaticTimer.current)
                programmaticTimer.current = window.setTimeout(() => {
                    programmatic.current = false
                }, 150)
                return
            }
            pinnedId.current = null
            syncActive()
        }

        root.addEventListener('scroll', onScroll, { passive: true })
        syncActive()
        return () => root.removeEventListener('scroll', onScroll)
    }, [getScrollRoot, syncActive])

    useEffect(() => {
        if (!activeId) return
        const bar = barRef.current
        const chip = chipRefs.current[activeId]
        if (!bar || !chip) return

        const barRect = bar.getBoundingClientRect()
        const chipRect = chip.getBoundingClientRect()
        const pad = 8
        if (chipRect.left < barRect.left + pad) {
            bar.scrollBy({ left: chipRect.left - barRect.left - pad, behavior: 'smooth' })
        } else if (chipRect.right > barRect.right - pad) {
            bar.scrollBy({ left: chipRect.right - barRect.right + pad, behavior: 'smooth' })
        }
    }, [activeId])

    useEffect(() => {
        return () => window.clearTimeout(programmaticTimer.current)
    }, [])

    const onChipClick = useCallback(
        (id: string) => {
            const root = getScrollRoot()
            const target = document.getElementById(overviewSectionDomId(id))
            if (!root || !target) return

            const tabsHeight = wrapRef.current?.offsetHeight ?? barRef.current?.offsetHeight ?? 0
            const nextTop =
                root.scrollTop +
                (target.getBoundingClientRect().top - root.getBoundingClientRect().top) -
                tabsHeight

            setActiveId(id)
            pinnedId.current = id
            programmatic.current = true
            window.clearTimeout(programmaticTimer.current)
            root.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' })
            programmaticTimer.current = window.setTimeout(() => {
                programmatic.current = false
            }, PROGRAMMATIC_SCROLL_MS)
        },
        [getScrollRoot]
    )

    if (sections.length === 0) return null

    return (
        <Box ref={wrapRef} className={classes.wrap}>
            <HorizontalScrollButtons containerRef={barRef} scrollAmount={500}>
                <Box ref={barRef} className={classes.bar}>
                    {sections.map((section) => {
                        const selected = section.id === activeId
                        return (
                            <Chip
                                key={section.id}
                                ref={(el) => {
                                    chipRefs.current[section.id] = el
                                }}
                                label={section.label}
                                size="small"
                                clickable
                                color={selected ? 'primary' : 'default'}
                                variant={selected ? 'filled' : 'outlined'}
                                className={cx(classes.chip, selected && classes.chipActive)}
                                onClick={() => onChipClick(section.id)}
                                data-event="Overview Section Tab Clicked"
                                data-event-section={section.label}
                            />
                        )
                    })}
                </Box>
            </HorizontalScrollButtons>
        </Box>
    )
})
